import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import { reportUnhandledError } from '@/lib/errors/unhandledErrors';

import { displayCssClass, effectiveBaseCssClass, isIconOnly } from './ngButtonBase';
import type { NgButtonBaseProps } from './ngButtonBase';

export type NgButtonProps = NgButtonBaseProps & {
  disabled?: boolean;
  submitButton?: boolean;
  form?: string;
  title?: string;
  ariaLabel?: string;
  ariaHasPopup?: boolean;
  ariaExpanded?: boolean;
  ariaControls?: string;
  testID?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  confirmationMessage?: string;
  enableMultipleClickPrevention?: boolean;
  showProgress?: boolean;
};

export function NgButton(props: NgButtonProps) {
  const {
    disabled,
    submitButton,
    form,
    title,
    ariaLabel,
    ariaHasPopup,
    ariaExpanded,
    ariaControls,
    testID,
    onClick,
    confirmationMessage,
    enableMultipleClickPrevention = true,
    showProgress = true,
    icon,
    text,
    children,
  } = props;

  const [isInProgress, setIsInProgress] = useState(false);
  const inProgressRef = useRef(false);

  const effectiveButtonType = submitButton ? 'submit' : 'button';
  const effectiveCssClass = `${displayCssClass(props)} ${effectiveBaseCssClass(props)}`;
  const loading = showProgress && isInProgress;

  const buttonClicked = async (event: MouseEvent<HTMLButtonElement>) => {
    if (enableMultipleClickPrevention && inProgressRef.current) {
      event.preventDefault();
      return;
    }

    inProgressRef.current = true;
    setIsInProgress(true);

    try {
      if (confirmationMessage == null || window.confirm(confirmationMessage)) {
        if (onClick) {
          await onClick(event);
        }
      }
    } catch (error) {
      console.error('Button click handler failed.', error);
      reportUnhandledError(error);
    } finally {
      inProgressRef.current = false;
      setIsInProgress(false);
    }
  };

  const screenReaderNameForIconOnlyButton = title;
  const effectiveAriaLabel = ariaLabel ?? (isIconOnly(props) ? screenReaderNameForIconOnlyButton : undefined);

  return (
    <button
      type={effectiveButtonType}
      className={`${effectiveCssClass} ng-button${loading ? ' ng-button--loading' : ''}`}
      disabled={disabled || (!submitButton && enableMultipleClickPrevention && isInProgress)}
      aria-busy={showProgress && isInProgress ? true : undefined}
      onClick={(event) => void buttonClicked(event)}
      title={title}
      form={form}
      aria-label={effectiveAriaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      data-testid={testID}
    >
      <span className="ng-button__content">
        {icon != null && <i className={`${icon} fa-lg`} aria-hidden="true" />}
        {children != null ? children : text}
      </span>
      <i className={`fal fa-spinner-third fa-lg ng-button__loader${loading ? ' fa-spin' : ''}`} aria-hidden="true" />
    </button>
  );
}
